import type { ReactNode } from "react";
import { fontSize, radius, space } from "@/theme/foundation";

export type StateTone = "neutral" | "error" | "info" | "success";

interface StateCardProps {
  icon: ReactNode;
  title: string;
  body: ReactNode;
  primary?: { label: string; onClick: () => void };
  secondary?: { label: string; onClick: () => void };
  tone?: StateTone;
  width?: number | string;
  height?: number | string;
}

const TONES: Record<StateTone, { bg: string; border: string; iconBg: string; iconColor: string }> = {
  neutral: {
    bg: "var(--canvas)",
    border: "var(--hairline)",
    iconBg: "var(--soft-cloud)",
    iconColor: "var(--ink)",
  },
  error: {
    bg: "var(--canvas)",
    border: "var(--error-border)",
    iconBg: "var(--error-bg)",
    iconColor: "var(--error)",
  },
  info: {
    bg: "var(--canvas)",
    border: "var(--hairline)",
    iconBg: "var(--info-bg)",
    iconColor: "var(--info)",
  },
  success: {
    bg: "var(--canvas)",
    border: "var(--success-border)",
    iconBg: "var(--success-bg)",
    iconColor: "var(--success)",
  },
};

export function StateCard({
  icon,
  title,
  body,
  primary,
  secondary,
  tone = "neutral",
  width = "100%",
  height = "auto",
}: StateCardProps) {
  const t = TONES[tone];
  return (
    <div
      style={{
        width,
        height,
        background: t.bg,
        border: `1px solid ${t.border}`,
      }}
      className="flex min-h-80 flex-col items-center justify-center gap-4 rounded-card p-6 text-center sm:p-8"
    >
      <span
        style={{
          width: 64,
          height: 64,
          borderRadius: radius.card,
          background: t.iconBg,
          color: t.iconColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </span>
      <div className="col gap-1.5" style={{ alignItems: "center", maxWidth: 360 }}>
        <h3
          style={{
            fontSize: fontSize.subheading,
            fontWeight: 700,
            letterSpacing: "-0.3px",
            margin: 0,
          }}
        >
          {title}
        </h3>
        <p
          style={{
            fontSize: fontSize.body,
            color: "var(--ash)",
            margin: 0,
            lineHeight: 1.65,
          }}
        >
          {body}
        </p>
      </div>
      {(primary || secondary) && (
        <div className="row flex-wrap justify-center gap-2.5" style={{ marginTop: space[2] }}>
          {secondary && (
            <button type="button" className="btn btn-secondary" onClick={secondary.onClick}>
              {secondary.label}
            </button>
          )}
          {primary && (
            <button type="button" className="btn btn-primary" onClick={primary.onClick}>
              {primary.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
